import { Box, Button, Flex, Group, Stack, Text } from "@mantine/core";
import { FC, useState } from "react";
import { ColorType } from "../FunnelMenu/ColorsPanel";
import { SPECIALTY_COLOR_MAP, STATUS_COLOR_MAP, TYPE_TITLE_MAP } from "../FunnelMenu/consts";
import { ReactComponent as ExpandIcon } from 'assets/caretleft.svg';
import { DEFAULT_PIN_COLOR } from "Modules/Map/MapComponent";
import { END_CHECK_IN_SCALE } from "../FunnelMenu/FiltersPanel";

interface Props {
  colorControl: ColorType;
  isVisible?: boolean;
  minScale?: number;
  maxScale?: number;
  showBorder?: boolean;
  isDesktop?: boolean;
};

const ColorLabel: FC<Props> = ({ colorControl, isVisible, minScale = 0, maxScale = END_CHECK_IN_SCALE, showBorder = true, isDesktop = false }) => {
  const [expanded, setExpanded] = useState(false);
  const isDiscrete = (colorControl === "specialty-type" || colorControl === "status");
  return (
    <>
      {isVisible ? (
        <Box
          bg="#4F7D9C"
          w="100%"
          h={expanded ? 'auto' : '100%'}
          style={{
            borderBottom: showBorder ? "2px solid #FF9D00" : "none"
          }}
        >
          {isDiscrete ? (
            <Flex
              pos="relative"
              px={10}
              py={10}
              my={0}
              gap={10}
              mah={expanded ? 9999 : 60}
              style={{
                overflowY: "hidden",
                transition: "max-height 0.3s ease-in-out",
              }}
            >
              {colorControl === "specialty-type" ? (
                <Flex flex="1 1 0" gap={8} align="center" justify="center" wrap="wrap">
                  {[...SPECIALTY_COLOR_MAP.entries()].map(([k, v], index) => (
                    <Group key={index} gap={3} align="center">
                      <Box w={10} h={10} bg={v}>
                      </Box>
                      <Text c="white" fz={8}>
                        {k}
                      </Text>
                    </Group>
                  ))}
                </Flex>
              ) : (
                <Flex flex="1 1 0" gap={8} align="center" justify="center" wrap="wrap"
                  style={{ boxSizing: "border-box" }}>
                  {[...STATUS_COLOR_MAP.entries()].map(([k, v], index) => (
                    <Group key={index} gap={3} align="center">
                      <Box w={10} h={10} bg={v}>
                      </Box>
                      <Text c="white" fz={8}>
                        {k}
                      </Text>
                    </Group>
                  ))}
                </Flex>
              )}
              {!isDesktop && (
                <Button
                  onClick={() => setExpanded((prev) => !prev)}
                  variant="subtle"
                  flex="0 0 auto"
                  px={0}
                  styles={{
                    label: {
                      flexDirection: 'column',
                      alignItems: 'center',
                    },
                  }}
                  color="white"
                >
                  <ExpandIcon
                    style={{
                      transform: expanded ? "rotate(270deg)" : "rotate(90deg)",
                      color: "white",
                    }}
                    stroke="white"
                  />
                  <Text c="white" fz={9}>
                    {expanded ? "MINIMIZE" : "EXPAND"}
                  </Text>
                </Button>
              )}
            </Flex>
          ) : (
            <Box h={60} >
              <Stack gap={0} align="center">
                <Text ta="center" c="white" fw="bold" fz={12} my={10}>
                  {TYPE_TITLE_MAP.get(colorControl)}
                </Text>
                <Box
                  h={10} w="70%" pos="relative"
                  bg="linear-gradient(90deg, #FF9D00 0%, #8B0000 100%)">
                  <Text pos="absolute" left={-20} top={-3} c="white" fz={10}>
                    {minScale}
                  </Text>
                  <Text pos="absolute" right={-35} top={-3} c="white" fz={10}>
                    {maxScale}
                  </Text>
                </Box>
              </Stack>
            </Box>
          )}
        </Box>
      ) : null}
    </>
  );
};


export const valueToGradientColor = (
  value: number,
  min: number = 0,
  max: number = END_CHECK_IN_SCALE
): string => {
  const clamped = Math.min(Math.max(value, min), max);

  const t = max > min ? (clamped - min) / (max - min) : 0; // 0 ≤ t ≤ 1

  const start = { r: 0xff, g: 0x9d, b: 0x00 }; // #FF9D00
  const end = { r: 0x8B, g: 0x00, b: 0x00 }; // #8B0000

  const lerp = (a: number, b: number) => Math.round(a + (b - a) * t);
  const r = lerp(start.r, end.r);
  const g = lerp(start.g, end.g);
  const b = lerp(start.b, end.b);

  const toHex = (n: number) => n.toString(16).padStart(2, '0').toUpperCase();

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const valueToColor = (v: string, colorControl: ColorType) => {
  if (colorControl === "specialty-type") {
    if (SPECIALTY_COLOR_MAP.get(v)) {
      return SPECIALTY_COLOR_MAP.get(v);
    }
  } else {
    if (STATUS_COLOR_MAP.get(v)) {
      return STATUS_COLOR_MAP.get(v);
    }
  }
  return DEFAULT_PIN_COLOR;
}


export default ColorLabel;
